import { BaseGameDrawer } from './BaseGameDrawer';
import { loadingStageService } from '../../shared/loading/LoadingStageService';
import { backgroundDrawer } from '../../shared/background/BackgroundDrawer';
import {
  bonusWheelDrawer,
  wheelDrawer,
} from '../../shared/wheel/WheelDrawer';
import { GAME_MODES, GAME_SCENE } from '../../core/constats';
import {
  DESKTOP_BONUS_CONTAINER_NAMES,
  DESKTOP_MAIN_CONTAINER_NAMES,
  DESKTOP_PRE_BONUS_CONTAINER_NAMES,
} from '../../core/desktopConstants';
import { oddCrystalsDrawer } from '../../shared/oddCrystals/OddCrystalsDrawer';
import { gamePadDrawer } from '../../shared/gamePad/GamePadDrawer';
import { bonusBoardDrawer } from '../../shared/bonusBoard/BonusBoardDrawer';
import { buyBonusDesktopPopup } from '../../shared/buyBonusPopup/drawers/BuyBonusDesktopPopup';
import { bigWinAnimationDrawer } from '../../shared/bigWinAnimation/BigWinAnimationDrawer';
import { preBonusContentDrawer } from '../../shared/preBonusContent/PreBonusContentDrawer';
import { bonusPadDrawer } from '../../shared/bonusPad/BonusPadDrawer';
import { congratulationsDesktopPopup } from '../../shared/congratulationsPopup/drawers/CongratulationsDesktopPopup';
import { gameConfig } from '../../core/configs';
import { bonusCongratulationsWebPopup } from '../../shared/bonusCongratulationsPopup/drawers/BonusCongratulationsWebPopup';

class GameDesktopDrawer extends BaseGameDrawer {
  constructor(drawers) {
    super(drawers);
    this.config = {
      mainContainers: [
        { drawer: 'background', containerName: DESKTOP_MAIN_CONTAINER_NAMES.BACKGROUND },
        { drawer: 'oddCrystal', containerName: DESKTOP_MAIN_CONTAINER_NAMES.ODD_CRYSTAL },
        { drawer: 'gamePad', containerName: DESKTOP_MAIN_CONTAINER_NAMES.GAME_PAD },
        { drawer: 'bonusBoard', containerName: DESKTOP_MAIN_CONTAINER_NAMES.BONUS_BOARD },
        { drawer: 'wheel', containerName: DESKTOP_MAIN_CONTAINER_NAMES.WHEEL },
      ],
      preBonusContainers: [
        { drawer: 'background', containerName: DESKTOP_PRE_BONUS_CONTAINER_NAMES.BACKGROUND },
        { drawer: 'gamePad', containerName: DESKTOP_PRE_BONUS_CONTAINER_NAMES.GAME_PAD },
        { drawer: 'preBonus', containerName: DESKTOP_PRE_BONUS_CONTAINER_NAMES.PRE_BONUS_CONTENT },
      ],
      bonusContainers: [
        { drawer: 'background', containerName: DESKTOP_BONUS_CONTAINER_NAMES.BACKGROUND },
        { drawer: 'gamePad', containerName: DESKTOP_BONUS_CONTAINER_NAMES.GAME_PAD },
        { drawer: 'bonusPad', containerName: DESKTOP_BONUS_CONTAINER_NAMES.BONUS_PAD },
        { drawer: 'bonusWheel', containerName: DESKTOP_BONUS_CONTAINER_NAMES.WHEEL },
      ],
    };

    this.ressourceLoaded = null;
  }

  _resetObserverOpenedState() {
    if (this.once.has('_resetObserverOpenedState')) {
      return;
    }
    const rect = this.createGraphics()
      .beginFill()
      .drawRect(0, 0, gameConfig.width, gameConfig.height)
      .endFill();
    this.parent.addChild(rect);
    rect.interactive = true;
    rect.buttonMode = false;
    rect.on('pointerdown', (event) => {
      event.stopPropagation();
      this.drawers.gamePad.resetOpenedState();
    });
    this.once.add('_resetObserverOpenedState');
  }

  defineMainSceneContainer() {
    this._defineContainers(
      this.config.mainContainers,
      this.getMainSceneContainer.bind(this),
      GAME_MODES.DESKTOP,
    );
  }

  setupMainSceneContainer(parent) {
    this._setupSceneContainer({
      parent,
      containerNames: [
        DESKTOP_MAIN_CONTAINER_NAMES.BACKGROUND,
        DESKTOP_MAIN_CONTAINER_NAMES.ODD_CRYSTAL,
        DESKTOP_MAIN_CONTAINER_NAMES.BONUS_BOARD,
        DESKTOP_MAIN_CONTAINER_NAMES.GAME_PAD,
        DESKTOP_MAIN_CONTAINER_NAMES.WHEEL,
        DESKTOP_MAIN_CONTAINER_NAMES.BUY_BONUS_POPUP,
        DESKTOP_MAIN_CONTAINER_NAMES.BIG_WIN,
        DESKTOP_MAIN_CONTAINER_NAMES.CONGRATULATIONS_BONUS_POPUP,
      ],
      thisTarget: 'mainContainers',
    });
  }

  setupPreBonusSceneContainer(parent) {
    this._setupSceneContainer({
      parent,
      containerNames: [
        DESKTOP_PRE_BONUS_CONTAINER_NAMES.BACKGROUND,
        DESKTOP_PRE_BONUS_CONTAINER_NAMES.GAME_PAD,
        DESKTOP_PRE_BONUS_CONTAINER_NAMES.PRE_BONUS_CONTENT,
      ],
      thisTarget: 'preBonusSceneContainers',
    });
  }

  definePreBonusSceneContainer() {
    this._defineContainers(
      this.config.preBonusContainers,
      this.getPreBonusSceneContainer.bind(this),
      GAME_MODES.DESKTOP,
    );
  }

  setupBonusSceneContainer(parent) {
    this._setupSceneContainer({
      parent,
      containerNames: [
        DESKTOP_BONUS_CONTAINER_NAMES.BACKGROUND,
        DESKTOP_BONUS_CONTAINER_NAMES.GAME_PAD,
        DESKTOP_BONUS_CONTAINER_NAMES.BONUS_PAD,
        DESKTOP_BONUS_CONTAINER_NAMES.WHEEL,
        DESKTOP_BONUS_CONTAINER_NAMES.GAME_PAD_FULL,
        DESKTOP_BONUS_CONTAINER_NAMES.BIG_WIN,
        DESKTOP_BONUS_CONTAINER_NAMES.CONGRATULATIONS_POPUP,
      ],
      thisTarget: 'bonusSceneContainers',
    });
  }

  defineBonusSceneContainer() {
    this._defineContainers(
      this.config.bonusContainers,
      this.getBonusSceneContainer.bind(this),
      GAME_MODES.DESKTOP,
    );
  }

  destroyMainSceneObservers() {
    this.drawers.background.application.destroy();
    this.drawers.oddCrystal.application.destroy();
    this.drawers.wheel.application.destroy();
    this.drawers.gamePad.application.destroy();
    this.drawers.bonusBoard.application.destroy();
  }

  destroyPreBonusSceneObservers() {
    this.drawers.background.application.destroy();
    this.drawers.gamePad.application.destroy();
    this.drawers.preBonus.application.destroy();
  }

  destroyBonusSceneObservers() {
    this.drawers.background.application.destroy();
    this.drawers.gamePad.application.destroy();
    this.drawers.bonusWheel.application.destroy();
    this.drawers.bonusPad.application.destroy();
  }

  executeMainScene() {
    super.executeBaseMainScene();
    this.getMainSceneContainer(DESKTOP_MAIN_CONTAINER_NAMES.BUY_BONUS_POPUP).children = [];
    this.getMainSceneContainer(DESKTOP_MAIN_CONTAINER_NAMES.BIG_WIN).children = [];
    this.drawers.background.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.MAIN });
    this.drawers.oddCrystal.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.MAIN });
    this.drawers.wheel.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.MAIN });
    this.drawers.gamePad.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.MAIN });
    this.drawers.bonusBoard.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.MAIN });
    this.drawers.buyBonusPopup.executeWeb({
      root: this.getMainSceneContainer(DESKTOP_MAIN_CONTAINER_NAMES.BUY_BONUS_POPUP),
    });
    this.drawers.bonusCongratulationsPopup.execute({
      root: this.getMainSceneContainer(DESKTOP_MAIN_CONTAINER_NAMES.BIG_WIN),
    });
    this.drawers.bigWin.execute(GAME_MODES.DESKTOP, {
      root: this.getMainSceneContainer(DESKTOP_MAIN_CONTAINER_NAMES.BIG_WIN),
    });
  }

  executePreBonusScene() {
    super.executeBasePreBonusScene();
    this.drawers.background.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.PRE_BONUS });
    this.drawers.gamePad.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.PRE_BONUS });
    this.drawers.preBonus.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.PRE_BONUS });
  }

  executeBonusScene() {
    super.executeBaseBonusScene();
    this.getBonusSceneContainer(DESKTOP_BONUS_CONTAINER_NAMES.BIG_WIN).children = [];
    this.getBonusSceneContainer(DESKTOP_BONUS_CONTAINER_NAMES.CONGRATULATIONS_POPUP).children = [];
    this.getBonusSceneContainer(DESKTOP_BONUS_CONTAINER_NAMES.GAME_PAD_FULL).children = [];
    this.drawers.background.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.BONUS });
    this.drawers.gamePad.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.BONUS });
    this.drawers.bonusWheel.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.BONUS });
    this.drawers.bonusPad.execute(GAME_MODES.DESKTOP, { scene: GAME_SCENE.BONUS });
    this.drawers.bigWin.execute(GAME_MODES.DESKTOP, {
      root: this.getBonusSceneContainer(DESKTOP_BONUS_CONTAINER_NAMES.BIG_WIN),
    });
    this.drawers.congratulationsPopup.executeWeb({
      root: this.getBonusSceneContainer(DESKTOP_BONUS_CONTAINER_NAMES.CONGRATULATIONS_POPUP),
    });
    this.drawers.gamePad.application.addBonusPressToStartLayer({
      root: this.getBonusSceneContainer(DESKTOP_BONUS_CONTAINER_NAMES.GAME_PAD_FULL),
    })
  }

  async loadResource() {
    if (!this.ressourceLoaded) {
      this.ressourceLoaded = Promise.all([
        this.drawers.background.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.wheel.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusWheel.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.oddCrystal.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.gamePad.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.bigWin.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusPad.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusBoard.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.preBonus.application.onLoad().then(() => loadingStageService.next()),
        this.drawers.buyBonusPopup.onLoad().then(() => loadingStageService.next()),
        this.drawers.congratulationsPopup.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusCongratulationsPopup.onLoad().then(() => loadingStageService.next()),
      ]);
    }
    await this.ressourceLoaded;
  }
}

export const gameDesktopDrawer = new GameDesktopDrawer({
  background: backgroundDrawer, //shared
  wheel: wheelDrawer,
  bonusWheel: bonusWheelDrawer,
  oddCrystal: oddCrystalsDrawer,
  gamePad: gamePadDrawer,
  bigWin: bigWinAnimationDrawer,
  bonusPad: bonusPadDrawer, // bonusPage
  bonusBoard: bonusBoardDrawer,
  buyBonusPopup: buyBonusDesktopPopup,
  preBonus: preBonusContentDrawer,
  congratulationsPopup: congratulationsDesktopPopup,
  bonusCongratulationsPopup: bonusCongratulationsWebPopup,
});
